import { ApiController } from '@/services/ApiController';
import { ApiRoutes } from '@/enums/api/ApiRoutes';
import { AxiosResponse } from 'axios';

export interface ProjectPreorderDraftFile {
    id: number;
    title: string;
    displayName: string | null;
    fileSize: string | number | null;
    fileType: string | null;
    documentType: string | null;
    url: string;
    positionKey: string | null;
}

export interface ProjectPreorderDraftPosition {
    key: string;
    system: string;
    quantity: number;
    attachments: ProjectPreorderDraftFile[];
}

export interface ProjectPreorderDraft {
    id: number;
    savedAt: string;
    currentStep: number;
    desiredDeliveryDate: string | null;
    positions: ProjectPreorderDraftPosition[];
}

export interface ProjectPreorderDraftResponse {
    connection: string | null;
    comment: string | null;
    draft: ProjectPreorderDraft | null;
}

export interface ProjectPreorderDraftPayload {
    connection: string | null;
    comment: string | null;
    desiredDeliveryDate: string | null;
    positions: { key: string; system: string; quantity: number }[];
    currentStep: number;
}

export default class ProjectPreorderDraftAPI {
    public static async get(projectId: string): Promise<ProjectPreorderDraftResponse> {
        const response = (await ApiController.get(ApiRoutes.projectPreorderDraft.byProject(projectId))) as AxiosResponse;
        return response.data;
    }

    public static async save(projectId: string, payload: ProjectPreorderDraftPayload): Promise<ProjectPreorderDraftResponse> {
        const response = (await ApiController.post(
            ApiRoutes.projectPreorderDraft.byProject(projectId),
            payload,
        )) as AxiosResponse;
        return response.data;
    }

    public static async remove(projectId: string): Promise<void> {
        await ApiController.delete(ApiRoutes.projectPreorderDraft.byProject(projectId));
    }

    public static async uploadFile(
        projectId: string,
        file: Blob,
        positionKey: string,
        documentType?: string | null,
    ): Promise<ProjectPreorderDraftFile> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('position_key', positionKey);
        if (documentType) {
            formData.append('document_type', documentType);
        }

        const response = (await ApiController.post(
            ApiRoutes.projectPreorderDraft.files(projectId),
            formData,
        )) as AxiosResponse;
        return response.data;
    }

    public static async updateProjectFileType(fileId: string, documentType: string | null): Promise<void> {
        await ApiController.patch(ApiRoutes.projectPreorderDraft.projectFileType(fileId), { documentType });
    }

    public static async updateFileType(fileId: string, documentType: string | null): Promise<void> {
        await ApiController.patch(ApiRoutes.projectPreorderDraft.fileType(fileId), { documentType });
    }

    public static async removeFile(fileId: string): Promise<void> {
        await ApiController.delete(ApiRoutes.projectPreorderDraft.file(fileId));
    }
}